import { useState } from 'react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';

interface BranchSyncDialogProps {
    isOpen: boolean;
    planId: string;
    planName: string;
    activeMembershipsCount: number;
    onClose: () => void;
    onSuccess: () => void;
}

export default function BranchSyncDialog({
    isOpen,
    planId,
    planName,
    activeMembershipsCount,
    onClose,
    onSuccess
}: BranchSyncDialogProps) {
    const [syncing, setSyncing] = useState(false);
    const [progress, setProgress] = useState<{ total: number; updated: number; errors: number } | null>(null);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error('No access token');
            }

            const apiUrl = API_BASE_URL;
            const response = await fetch(`${apiUrl}/api/member-plans/${planId}/sync-member-branches`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProgress(data.data);
                toast.success(data.message || 'Members synchronized successfully!');
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 2000);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to sync members');
            }
        } catch (error) {
            console.error('Error syncing members:', error);
            toast.error('Failed to sync members');
        } finally {
            setSyncing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1100] p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Update Existing Memberships?
                    </h3>
                    <p className="text-sm text-gray-600">
                        You've changed the branch assignments for <span className="font-semibold">{planName}</span>.
                    </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <p className="text-sm text-blue-900 font-medium mb-1">
                                Branch changes apply to new memberships only
                            </p>
                            <p className="text-xs text-blue-800">
                                You can optionally update branch access for all {activeMembershipsCount} active memberships now.
                            </p>
                        </div>
                    </div>
                </div>

                {progress ? (
                    <div className="mb-6">
                        <div className="bg-gray-100 rounded-lg p-4">
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <div className="text-2xl font-bold text-blue-600">{progress.total}</div>
                                    <div className="text-xs text-gray-600">Total</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-green-600">{progress.updated}</div>
                                    <div className="text-xs text-gray-600">Updated</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-red-600">{progress.errors}</div>
                                    <div className="text-xs text-gray-600">Errors</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={syncing}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {progress ? 'Close' : 'Skip'}
                    </button>
                    {!progress && (
                        <button
                            onClick={handleSync}
                            disabled={syncing}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                        >
                            {syncing && (
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                            <span>{syncing ? 'Synchronizing...' : 'Yes, Update Now'}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
